import { BetrokkenTrein } from "@/entities/betrokken-treinen";
import { BusEvacuatieActieDetailsDto, BusEvacuatieActieDto } from "@/shared/types";
import { parseISO } from "date-fns";
import { ActieDtoTransformatieError } from "./actie-error";
import { BaseActie } from "./base-actie";

export class BusEvacuatieActieTransformatieError extends ActieDtoTransformatieError {
    private constructor(wijzigingType: BaseActie["wijzigingType"], reason: string) {
        super("BusEvacuatie", wijzigingType, reason);
    }

    public static geenNieuweWaardeVoorToegevoegd(): BusEvacuatieActieTransformatieError {
        return new BusEvacuatieActieTransformatieError("Toegevoegd", `nieuweWaarde vereist is, maar deze is: nieuweWaarde=null`);
    }

    public static geenNieuweWaardeVoorGewijzigd(): BusEvacuatieActieTransformatieError {
        return new BusEvacuatieActieTransformatieError("Gewijzigd", `nieuweWaarde vereist is, maar deze is: nieuweWaarde=null`);
    }
}

export type GewijzigdeBusEvacuatieActieVelden = "OpstelLocatie" | "AantalBussen" | "AfzetStation";
type BusEvacuatieUitvoerder = "DeWit" | "Transvision";
type BusEvacuatieActieDetails = {
    treinId: number;
    treinType: string;
    treinNummer: number;
    gewijzigdeVelden: GewijzigdeBusEvacuatieActieVelden[];
    uitvoerder: BusEvacuatieUitvoerder;
};

const BusEvacuatieActieDetails = {
    fromDto: (dto: BusEvacuatieActieDetailsDto): BusEvacuatieActieDetails => ({
        gewijzigdeVelden: dto.gewijzigdeVelden,
        uitvoerder: dto.uitvoerder as BusEvacuatieActieDetails["uitvoerder"],
        treinId: dto.treinId,
        treinType: dto.treinType ?? "",
        treinNummer: dto.treinNummer ?? 0,
    }),
};

export type BusEvacuatieToegevoegdActie = BaseActie & {
    type: "BusEvacuatie";
    wijzigingType: "Toegevoegd";
    nieuweWaarde: BusEvacuatieActieDetails;
    uitvoerder: BusEvacuatieUitvoerder;
    treinId: number;
};

export const BusEvacuatieToegevoegdActie = {
    fromDto(dto: BusEvacuatieActieDto): BusEvacuatieActie {
        if (!dto.nieuweWaarde) {
            throw BusEvacuatieActieTransformatieError.geenNieuweWaardeVoorToegevoegd();
        }

        const busEvacuatieActieDetails = BusEvacuatieActieDetails.fromDto(dto.nieuweWaarde);

        return {
            id: dto.id,
            type: "BusEvacuatie",
            timelineContent: {
                titel: "Evacuatiebus besteld",
                beschrijving: `De regievoerder heeft een evacuatiebus aangevraagd voor trein ${BetrokkenTrein.formatTrainLabel(busEvacuatieActieDetails)}`,
                actieTekst: "Bekijk openstaande aanvraag",
            },
            verstuurdOpDatumTijd: parseISO(dto.verstuurdOpDatumTijd),
            toelichting: dto.toelichting,
            status: dto.status,
            wijzigingType: "Toegevoegd",
            nieuweWaarde: busEvacuatieActieDetails,
            uitvoerder: dto.uitvoerder as BusEvacuatieUitvoerder,
            treinId: dto.treinId,
        };
    },
};

export type BusEvacuatieGewijzigdActie = BaseActie & {
    type: "BusEvacuatie";
    wijzigingType: "Gewijzigd";
    nieuweWaarde: BusEvacuatieActieDetails;
    uitvoerder: BusEvacuatieUitvoerder;
    treinId: number;
};

export const BusEvacuatieGewijzigdActie = {
    fromDto(dto: BusEvacuatieActieDto): BusEvacuatieActie {
        if (!dto.nieuweWaarde) {
            throw BusEvacuatieActieTransformatieError.geenNieuweWaardeVoorGewijzigd();
        }

        const busEvacuatieActieDetails = BusEvacuatieActieDetails.fromDto(dto.nieuweWaarde);

        return {
            id: dto.id,
            type: "BusEvacuatie",
            timelineContent: {
                titel: "Wijziging evacuatiebus aangevraagd",
                beschrijving: `De regievoerder heeft de aanvraag voor de evacuatiebus van trein ${BetrokkenTrein.formatTrainLabel(busEvacuatieActieDetails)} gewijzigd`,
                actieTekst: "Bekijk evacuatiebus",
            },
            verstuurdOpDatumTijd: parseISO(dto.verstuurdOpDatumTijd),
            toelichting: dto.toelichting,
            status: dto.status,
            wijzigingType: "Gewijzigd",
            nieuweWaarde: busEvacuatieActieDetails,
            uitvoerder: dto.uitvoerder as BusEvacuatieUitvoerder,
            treinId: dto.treinId,
        };
    },
};

export type BusEvacuatieActie = BusEvacuatieToegevoegdActie | BusEvacuatieGewijzigdActie;

export const BusEvacuatieActie = {
    fromDto: (dto: BusEvacuatieActieDto): BusEvacuatieActie => {
        switch (dto.wijzigingType) {
            case "Toegevoegd":
                return BusEvacuatieToegevoegdActie.fromDto(dto);
            case "Gewijzigd":
                return BusEvacuatieGewijzigdActie.fromDto(dto);
            default:
                throw ActieDtoTransformatieError.nietOndersteundWijzigingTypeVoorActie(dto.type, dto.wijzigingType);
        }
    },
};

export type BusEvacuatieGeannuleerdActie = BaseActie & {
    type: "AnnuleerBusEvacuatieActie";
    wijzigingType: "Toegevoegd";
    nieuweWaarde: BusEvacuatieActieDetails;
    uitvoerder: BusEvacuatieUitvoerder;
};

export const BusEvacuatieGeannuleerdActie = {
    fromDto(dto: BusEvacuatieActieDto): BusEvacuatieGeannuleerdActie {
        if (!dto.nieuweWaarde) {
            throw BusEvacuatieActieTransformatieError.geenNieuweWaardeVoorToegevoegd();
        }

        const busEvacuatieActieDetails = BusEvacuatieActieDetails.fromDto(dto.nieuweWaarde);

        return {
            id: dto.id,
            type: "AnnuleerBusEvacuatieActie",
            timelineContent: {
                titel: "Evacuatiebus ingetrokken",
                beschrijving: `De regievoerder heeft de evacuatiebus voor trein  ${BetrokkenTrein.formatTrainLabel(busEvacuatieActieDetails)} ingetrokken`,
                actieTekst: "Bekijk evacuatiebus",
            },
            verstuurdOpDatumTijd: parseISO(dto.verstuurdOpDatumTijd),
            toelichting: dto.toelichting,
            status: dto.status,
            wijzigingType: "Toegevoegd",
            nieuweWaarde: busEvacuatieActieDetails,
            uitvoerder: dto.uitvoerder as BusEvacuatieUitvoerder,
        };
    },
};
